import { useDefinitionsActivitiesAndCategoriesQuery } from "@Components/shared/queries";
import { IGroupedRadialBarChartData } from "@Components/shared/GroupedRadialBarChart";
import { IKeyedArray } from "@Components/shared/types";
import { IActivityCategoryDefinition, IActivityDetails } from "@Models/IActivityDetails";
import { DateTime, arrayToMapByKey, getDuration, isNil } from "@Utils";

/**
 * Interface for the return value of useCPDChartValues hook
 */
interface IUseCPDChartValues {
    /**
     * Processed data for chart and table
     */
    pointsData: {
        chartData: IGroupedRadialBarChartData[];
        tableData: IKeyedArray;
    };
    /**
     * Map of points values
     */
    pointsValuesMap: Map<string, number[]>;

    /**
     * Flag to indicate if the data has been fetched and populated
     */
    isPointsDataHydrated?: boolean;
}

/**
 * Interface for the points category map values
 */
interface IPointsCategory {
    activitySet: Set<string>;
    value: number;
}

/**
 * Custom hook to process CPD (Continuing Professional Development) activities data
 * for chart and table representation
 *
 * @param activities - Array of activity details
 * @returns Processed data for chart and table, along with a map of points values
 */
const useCPDChartValues = (activities: IActivityDetails[]): IUseCPDChartValues => {
    const { activitiesMap, categoriesMap, isFetched } = useDefinitionsActivitiesAndCategoriesQuery();

    const pointsValues: (IGroupedRadialBarChartData & { id: string })[] = [];
    const pointsCategoryMap = new Map<string, IPointsCategory>();

    // Create a map of activity IDs to their respective points
    const pointsValuesMap = arrayToMapByKey(activities, (activity) => activity.activityId, false, "points");
    // Group activities by category
    const activitiesByCategory = arrayToMapByKey(activities, (activity) => activity.categoryLabel, false);

    /**
     * Recursive function to add parent categories to the category list
     * @param categories - Array to store category hierarchy
     * @param category - Current category definition
     */
    const addParentCategory = (categories: string[], category: IActivityCategoryDefinition) => {
        if (
            isNil(category?.parentCategoryId) ||
            isNil(category?.activityCategoryId) ||
            category?.parentCategoryId === category?.activityCategoryId
        )
            return;
        const parentCategory = categoriesMap.get(category.parentCategoryId);
        categories.unshift(category.label);

        if (!parentCategory) return;

        addParentCategory(categories, parentCategory);
    };

    // Process each activity and calculate points
    pointsValuesMap.forEach((pointsArray, activityId) => {
        const value = Math.floor(pointsArray.reduce((a, b) => a + b, 0));
        if (value === 0) {
            return;
        }

        const activity = activitiesMap.get(activityId);

        const activityCategoryId = activity?.activityCategoryId;

        if (!activityCategoryId) {
            return;
        }

        const groupCategory = categoriesMap.get(activityCategoryId);
        const categories: string[] = [];

        // Update or create points category
        const pointsCategory = pointsCategoryMap.get(activityCategoryId);
        if (pointsCategory) {
            pointsCategory.activitySet.add(activityId);

            if (!pointsCategory.activitySet.has(activityCategoryId)) {
                pointsCategory.activitySet.add(activityId);
                pointsCategory.value += value;
            }
        } else {
            pointsCategoryMap.set(activityCategoryId, { activitySet: new Set([activityId]), value });
        }

        const activitySet = pointsCategoryMap.get(activityCategoryId)?.activitySet;

        if (!activitySet || !groupCategory) {
            return;
        }

        const index = Array.from(activitySet)?.indexOf(activityId);
        addParentCategory(categories, groupCategory);

        // Add processed data to pointsValues array
        pointsValues.push({
            group: groupCategory?.label,
            groups: categories,
            id: activity?.activityCategoryId,
            index,
            item: activity?.label,
            value: value,
        });
    });

    // Prepare chart data
    const chartData: IGroupedRadialBarChartData[] = pointsValues.map(({ id, ...otherValues }) => ({
        groupValue: pointsCategoryMap.get(id)?.value,
        ...otherValues,
    }));

    // Prepare table data
    const tableData: IKeyedArray = {};
    activitiesByCategory.forEach((details, key) => {
        tableData[key] = details
            .sort(
                (a, b) =>
                    a.activityId.localeCompare(b.activityId) ||
                    parseInt(b.creationDate, 10) - parseInt(a.creationDate, 10)
            )
            .map((detail) => ({
                activity: detail.activityLabel,
                date: DateTime(detail.creationDate).format("MMM Do YYYY"),
                duration: getDuration(detail.duration),
                item: detail.activityLabel, // needed for chart table
                key: detail.activityInstanceId,
            }));
    });

    return { pointsData: { chartData, tableData }, pointsValuesMap, isPointsDataHydrated: isFetched };
};

export default useCPDChartValues;
